<template>
  <div
    class="dark:text-content-level-400 h-5 text-content-level-500 w-5"
    :aria-label="ariaLabel"
  >
    <!-- Gluten-Free -->
    <svg
      v-if="name === 'gluten-free'"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      :aria-label="$t('common.dietary.glutenFree')"
    >
      <circle
        cx="12"
        cy="12"
        r="9"
      />
      <!-- G -->
      <path d="M9 9.5c-1.5 0-2.5 1-2.5 2.5s1 2.5 2.5 2.5c1 0 1.5-.5 1.5-.5v-1.5H9.5" />
      <!-- F -->
      <path d="M13.5 9.5v5m0-5h3m-3 2.5h2" />
    </svg>

    <!-- Dairy-Free -->
    <svg
      v-else-if="name === 'dairy-free'"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      class="lucide lucide-milk-off lucide-milk-off-icon"
    >
      <path d="M8 2h8" />

      <path
        d="M9 2v1.343M15 2v2.789a4 4 0 0 0 .672 2.219l.656.984a4 4 0 0 1 .672 2.22v1.131M7.8 7.8l-.128.192A4 4 0 0 0 7 10.212V20a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2v-3"
      />

      <path d="M7 15a6.47 6.47 0 0 1 5 0 6.472 6.472 0 0 0 3.435.435" />

      <line
        x1="2"
        x2="22"
        y1="2"
        y2="22"
      />
    </svg>

    <!-- Nut-Free -->
    <svg
      v-else-if="name === 'nut-free'"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      class="lucide lucide-nut-off lucide-nut-off-icon"
    >
      <path d="M12 4V2" />

      <path
        d="M5 10v4a7.004 7.004 0 0 0 5.277 6.787c.412.104.802.292 1.102.592L12 22l.621-.621c.3-.3.69-.488 1.102-.592a7.01 7.01 0 0 0 4.125-2.939"
      />

      <path d="M19 10v3.343" />

      <path
        d="M12 12c-1.349-.573-1.905-1.005-2.5-2-.546.902-1.048 1.353-2.5 2-1.018-.644-1.46-1.08-2-2-1.028.71-1.69.918-3 1 1.081-1.048 1.757-2.03 2-3 .194-.776.84-1.551 1.79-2.21m11.654 5.997c.887-.457 1.28-.891 1.556-1.787 1.032.916 1.683 1.157 3 1-1.297-1.036-1.758-2.03-2-3-.5-2-4-4-8-4-.74 0-1.461.068-2.15.192"
      />

      <line
        x1="2"
        x2="22"
        y1="2"
        y2="22"
      />
    </svg>

    <!-- Vegetarian -->
    <svg
      v-else-if="name === 'vegetarian'"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      class="lucide lucide-leafy-green lucide-leafy-green-icon"
    >
      <path
        d="M2 22c1.25-.987 2.27-1.975 3.9-2.2a5.56 5.56 0 0 1 3.8 1.5 4 4 0 0 0 6.187-2.353 3.5 3.5 0 0 0 3.69-5.116A3.5 3.5 0 0 0 20.95 8 3.5 3.5 0 1 0 16 3.05a3.5 3.5 0 0 0-5.831 1.373 3.5 3.5 0 0 0-5.116 3.69 4 4 0 0 0-2.348 6.155C3.499 15.42 4.409 16.712 4.2 18.1 3.926 19.743 3.014 20.732 2 22"
      />

      <path d="M2 22 17 7" />
    </svg>

    <!-- Vegan -->
    <svg
      v-else-if="name === 'vegan'"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      class="lucide lucide-vegan lucide-vegan-icon"
    >
      <path d="M16 8q6 0 6-6-6 0-6 6" />

      <path d="M17.41 3.59a10 10 0 1 0 3 3" />

      <path d="M2 2a26.6 26.6 0 0 1 10 20c.9-6.82 1.5-9.5 4-14" />
    </svg>

    <!-- Low-Carb -->
    <svg
      v-else-if="name === 'low-carb'"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      class="lucide lucide-candy-off lucide-candy-off-icon"
    >
      <path d="M10 10v7.9" />

      <path d="M11.802 6.145a5 5 0 0 1 6.053 6.053" />

      <path d="M14 6.1v2.243" />

      <path d="m15.5 15.571-.964.964a5 5 0 0 1-7.071 0 5 5 0 0 1 0-7.07l.964-.965" />

      <path
        d="M16 7V3a1 1 0 0 1 1.707-.707 2.5 2.5 0 0 0 2.152.717 1 1 0 0 1 1.131 1.131 2.5 2.5 0 0 0 .717 2.152A1 1 0 0 1 21 8h-4"
      />

      <path d="m2 2 20 20" />

      <path
        d="M8 17v4a1 1 0 0 1-1.707.707 2.5 2.5 0 0 0-2.152-.717 1 1 0 0 1-1.131-1.131 2.5 2.5 0 0 0-.717-2.152A1 1 0 0 1 3 16h4"
      />
    </svg>

    <!-- Keto -->
    <svg
      v-else-if="name === 'keto'"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      class="lucide lucide-salad lucide-salad-icon"
    >
      <path d="M7 21h10" />

      <path d="M12 21a9 9 0 0 0 9-9H3a9 9 0 0 0 9 9Z" />

      <path
        d="M11.38 12a2.4 2.4 0 0 1-.4-4.77 2.4 2.4 0 0 1 3.2-2.77 2.4 2.4 0 0 1 3.47-.63 2.4 2.4 0 0 1 3.37 3.37 2.4 2.4 0 0 1-1.1 3.7 2.51 2.51 0 0 1 .03 1.1"
      />

      <path d="m13 12 4-4" />

      <path d="M10.9 7.25A3.99 3.99 0 0 0 4 10c0 .73.2 1.41.54 2" />
    </svg>

    <!-- Halal -->
    <svg
      v-else-if="name === 'halal'"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      :aria-label="$t('common.dietary.halal')"
    >
      <circle
        cx="12"
        cy="12"
        r="9"
      />

      <path d="M9 8v8M15 8v8M9 12h6" />
    </svg>

    <!-- Kosher -->
    <svg
      v-else-if="name === 'kosher'"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      :aria-label="$t('common.dietary.kosher')"
    >
      <circle
        cx="12"
        cy="12"
        r="9"
      />

      <path d="M9 7v10M9 12l6-5M9 12l6 5" />
    </svg>
  </div>
</template>

<script setup lang="ts">
type DietName =
  | 'gluten-free'
  | 'dairy-free'
  | 'nut-free'
  | 'vegetarian'
  | 'vegan'
  | 'low-carb'
  | 'keto'
  | 'halal'
  | 'kosher'

defineProps<{
  /** Dietary restriction represented by the icon. */
  name: DietName
  /** Accessible label for the dietary restriction icon. */
  ariaLabel?: string
}>()
</script>
